import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import { SearchField } from '@/design-system/search-field';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { Tabs, TabsList, TabsTrigger } from '@/design-system/tabs';
import { toast } from '@/design-system/toaster';
import { getListProvider } from '@/services/api/providers';
import {
  stopWorkflowsAPI,
  WORKFLOW_STATUS_LABELS,
  WORKFLOW_EXIM_LABELS,
  WORKFLOW_STOP_INTENT_LABELS,
  WORKFLOW_STOP_TYPE_LABELS,
  WorkflowStatus,
  WorkflowStopType,
  WorkflowSummary,
} from '@/services/api/stopWorkflows';
import { useUrlParam, useUrlPatch, useUrlSearch } from '@/hooks/urlState';

import WorkflowBuilderModal from './components/WorkflowBuilderModal';
import WorkflowFilterPanel, {
  countActiveFilters,
  EMPTY_FILTERS,
  WorkflowFilterValue,
} from './components/WorkflowFilterPanel';

type TabKey = 'active' | 'inactive' | 'all';

const TABS: { key: TabKey; label: string; status?: WorkflowStatus }[] = [
  { key: 'active', label: 'Active', status: 'ACTIVE' },
  { key: 'inactive', label: 'Inactive', status: 'INACTIVE' },
  { key: 'all', label: 'All' },
];

const TAB_KEYS = TABS.map((t) => t.key) as readonly TabKey[];
// Switching a status tab clears the search term (keeps filter-popover filters).
const TAB_RESET_PARAMS = ['q'] as const;

/** URL param names for the applied filters (BE query-param names). */
const FILTER_PARAMS = {
  clientId: 'clientID',
  stopType: 'stopType',
} as const;

const STOP_TYPES: WorkflowStopType[] = ['PICKUP', 'DROP_OFF'];

const WorkflowsPage: React.FC = () => {
  const { searchParams, patch: patchUrl } = useUrlPatch();

  const [tab, setTab] = useUrlParam<TabKey>('tab', 'active', {
    allowed: TAB_KEYS,
    history: 'push',
    resetParams: TAB_RESET_PARAMS,
  });
  // Client-side search; URL is the source of truth and the box resyncs on
  // external URL changes (tab switch drops `q`, Back/forward).
  const { value: search, setValue: setSearch, committed } = useUrlSearch('q', {
    delay: 200,
  });
  const debouncedSearch = committed.toLowerCase();

  // Applied filters = the URL. Client + stop type filter SERVER-side; the
  // search box stays client-side over the returned rows.
  const filters: WorkflowFilterValue = useMemo(() => {
    const stopType = searchParams.get(FILTER_PARAMS.stopType);
    return {
      clientId: searchParams.get(FILTER_PARAMS.clientId) ?? '',
      stopType: STOP_TYPES.includes(stopType as WorkflowStopType)
        ? (stopType as WorkflowStopType)
        : '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [filterDraft, setFilterDraft] =
    useState<WorkflowFilterValue>(EMPTY_FILTERS);

  const [items, setItems] = useState<WorkflowSummary[]>([]);
  const [tableLoading, setTableLoading] = useState(true);

  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Fallback name source for rows whose frozen snapshot is null (written
  // before the snapshot column existed, or core-service was unreachable at
  // write time). Best-effort — a failure just falls through to the id.
  const [clientNames, setClientNames] = useState<Map<number, string>>(
    () => new Map()
  );

  useEffect(() => {
    let cancelled = false;
    getListProvider({ page: '1', size: '200' })
      .then((res) => {
        if (cancelled) return;
        setClientNames(new Map(res.data.map((p) => [p.id, p.name])));
      })
      .catch(() => {
        // Names are cosmetic here — the snapshot is the real source.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchList = useCallback(async () => {
    setTableLoading(true);
    try {
      const status = TABS.find((t) => t.key === tab)?.status;
      const clientID = Number(filters.clientId);
      const data = await stopWorkflowsAPI.list({
        ...(status ? { status } : {}),
        ...(filters.clientId && Number.isFinite(clientID) && clientID > 0
          ? { clientID }
          : {}),
        ...(filters.stopType ? { stopType: filters.stopType } : {}),
      });
      setItems(data);
    } catch (err) {
      setItems([]);
      toast.error('Could not load workflows', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setTableLoading(false);
    }
  }, [tab, filters.clientId, filters.stopType]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const [promotingId, setPromotingId] = useState<string | null>(null);

  /**
   * Promote a workflow to its slot's default. The list is refetched rather than
   * patched locally: promotion demotes a sibling row too, and guessing which
   * one would drift from the server if the slot changed under us.
   */
  const promoteToDefault = async (workflow: WorkflowSummary) => {
    setPromotingId(workflow.id);
    try {
      await stopWorkflowsAPI.setDefault(workflow.id);
      toast.success('Default diperbarui', {
        description: `"${workflow.name}" sekarang dipakai otomatis untuk stop ini.`,
      });
      await fetchList();
    } catch (err) {
      toast.error('Gagal menjadikan default', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setPromotingId(null);
    }
  };

  /**
   * Frozen snapshot first (no fan-out, correct even after a master rename),
   * then the live master list, then the bare id.
   */
  const clientLabel = useCallback(
    (workflow: WorkflowSummary) =>
      // A GLOBAL workflow (clientID null) belongs to no client.
      workflow.clientID == null
        ? 'Semua client (Global)'
        : (workflow.client?.name ??
          clientNames.get(workflow.clientID) ??
          `Client #${workflow.clientID}`),
    [clientNames]
  );

  const filteredItems = useMemo(() => {
    if (!debouncedSearch) return items;
    return items.filter((wf) => {
      const haystack = [wf.name, String(wf.clientID), clientLabel(wf)]
        .join(' ')
        .toLowerCase();
      return haystack.includes(debouncedSearch);
    });
  }, [items, debouncedSearch, clientLabel]);

  // ── Filter popover handlers (mirror shipments) ──
  const activeFilterCount = useMemo(
    () => countActiveFilters(filters),
    [filters]
  );

  // Seed the draft from the applied filters whenever the popover opens.
  const handleFilterOpenChange = (open: boolean) => {
    if (open) setFilterDraft(filters);
    setFilterOpen(open);
  };

  // Write every filter field in ONE URL patch (atomic — one refetch).
  const writeFilters = (v: WorkflowFilterValue) => {
    patchUrl((params) => {
      const setOrDelete = (key: string, value: string) => {
        if (value) params.set(key, value);
        else params.delete(key);
      };
      setOrDelete(FILTER_PARAMS.clientId, v.clientId);
      setOrDelete(FILTER_PARAMS.stopType, v.stopType);
    });
  };

  const applyFilter = () => {
    writeFilters(filterDraft);
    setFilterOpen(false);
  };

  const resetFilter = () => {
    setFilterDraft(EMPTY_FILTERS);
    writeFilters(EMPTY_FILTERS);
    setFilterOpen(false);
  };

  const openCreate = () => {
    setEditingId(null);
    setBuilderOpen(true);
  };

  const openEdit = (workflow: WorkflowSummary) => {
    setEditingId(workflow.id);
    setBuilderOpen(true);
  };

  const nameCls = 'text-[13px] font-medium text-text-strong-950 tracking-tight';
  const metaCls = 'text-[11px] text-text-sub-600';
  const numCls = 'font-mono text-[12px] text-text-strong-950 tabular-nums';

  const columns: TableDataColumn<WorkflowSummary>[] = [
    {
      key: 'name',
      title: 'Workflow',
      render: (_v, record) => <span className={nameCls}>{record.name}</span>,
    },
    {
      key: 'client',
      title: 'Client',
      render: (_v, record) => (
        <span className="text-[13px] text-text-strong-950 tracking-tight">
          {clientLabel(record)}
        </span>
      ),
    },
    {
      key: 'stopType',
      title: 'Stop type',
      render: (_v, record) => (
        <div className="flex flex-wrap items-center gap-1">
          <Badge
            status={record.stopType === 'PICKUP' ? 'information' : 'feature'}
            appearance="lighter"
          >
            {WORKFLOW_STOP_TYPE_LABELS[record.stopType]}
          </Badge>
          {record.stopType === 'DROP_OFF' && record.intent !== 'GENERIC' && (
            <Badge status="neutral" appearance="lighter">
              {WORKFLOW_STOP_INTENT_LABELS[record.intent]}
            </Badge>
          )}
          {record.exim && record.exim !== 'GENERAL' && (
            <Badge
              status={record.exim === 'IMPORT' ? 'information' : 'feature'}
              appearance="lighter"
            >
              {WORKFLOW_EXIM_LABELS[record.exim]}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'milestoneCount',
      title: 'Milestones',
      render: (_v, record) => (
        <span className={numCls}>{record.milestoneCount}</span>
      ),
    },
    {
      key: 'fieldCount',
      title: 'Fields',
      render: (_v, record) => (
        <span className={numCls}>{record.fieldCount}</span>
      ),
    },
    {
      key: 'workflowStatus',
      title: 'Status',
      render: (_v, record) => (
        <div className="flex items-center gap-1.5">
          <Badge
            status={record.status === 'ACTIVE' ? 'success' : 'neutral'}
            type="dot"
          >
            {WORKFLOW_STATUS_LABELS[record.status]}
          </Badge>
          {/* Several workflows can be active in one slot; the badge is the only
              thing distinguishing the one shipments resolve to by default. */}
          {record.isDefault && record.status === 'ACTIVE' && (
            <Badge status="feature" appearance="lighter">
              Default
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'updatedAt',
      title: 'Updated',
      render: (_v, record) => (
        <span className={`${metaCls} tabular-nums`}>
          {new Date(record.updatedAt).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (_v, record) => (
        <div className="flex items-center justify-end gap-1.5">
          {/* Promotion is its own endpoint, not a field on the full-replace
              update — it carries no milestones, so it cannot revert a
              colleague's concurrent definition edit. */}
          {record.status === 'ACTIVE' && !record.isDefault && (
            <Button
              tone="neutral"
              style="stroke"
              size="xs"
              disabled={promotingId === record.id}
              onClick={(e) => {
                e.stopPropagation();
                void promoteToDefault(record);
              }}
            >
              {promotingId === record.id ? 'Menjadikan…' : 'Jadikan default'}
            </Button>
          )}
          <Button
            tone="primary"
            style="stroke"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              openEdit(record);
            }}
          >
            Edit
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="label-eyebrow mb-2">
            <span className="text-accent font-bold mr-3">05</span>
            Master data · Stop workflow
          </p>
          <h1 className="text-[28px] font-extrabold tracking-tight text-text-strong-950 leading-none">
            Workflows
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-text-sub-600 leading-relaxed">
            Per-client door-side forms: the milestones a rider completes at a
            stop, and the evidence each one requires. One active workflow per
            client and stop type; stops without one fall back to the app&apos;s
            static flow.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button tone="neutral" style="stroke" onClick={fetchList}>
            <i className="ri-refresh-line mr-1.5 text-[14px]" />
            Refresh
          </Button>
          <Button onClick={openCreate}>
            <i className="ri-add-line mr-1.5 text-[16px]" />
            New workflow
          </Button>
        </div>
      </div>

      <Card padding="none">
        {/* items-end so the active-tab underline sits on the card hairline */}
        <div className="flex flex-col gap-3 border-b border-stroke-soft-200 px-4 pt-1 pb-3 lg:flex-row lg:items-end lg:justify-between lg:pb-0">
          <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
            <TabsList variant="line" className="w-auto border-0">
              {TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="flex flex-1 items-center justify-end gap-2 lg:py-2">
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Search name or client…"
              size="sm"
              className="max-w-xs flex-1"
            />
            <WorkflowFilterPanel
              open={filterOpen}
              onOpenChange={handleFilterOpenChange}
              activeFilterCount={activeFilterCount}
              value={filterDraft}
              onChange={setFilterDraft}
              onApply={applyFilter}
              onReset={resetFilter}
            />
          </div>
        </div>

        <div className="p-4">
          <TableData<WorkflowSummary>
            data={filteredItems}
            columns={columns}
            loading={tableLoading}
            size="sm"
            onRowClick={(record) => openEdit(record)}
          />
        </div>
      </Card>

      <WorkflowBuilderModal
        isOpen={builderOpen}
        workflowId={editingId}
        onClose={() => {
          setBuilderOpen(false);
          setEditingId(null);
        }}
        onSaved={() => {
          setBuilderOpen(false);
          setEditingId(null);
          fetchList();
        }}
      />
    </div>
  );
};

export default WorkflowsPage;
